import React, { useState, useEffect, useContext } from "react";
import { Button, CircularProgress, Grid } from "@mui/material";

import { CREATE_FIELD, EDIT_FIELD } from "../../../../graphql/fieldGraphql";
import { DELETE_IMAGE, UPLOAD_IMAGE } from "../../../../graphql/imageGraphql";

import Form from "../../../../Components/Form/Form";
import {
  formValidator,
  isEmpty,
  isEmptyObject,
} from "../../../../Components/Form/validators";
import DrawerForm from "../../../../Components/Common/DrawerForm/DrawerForm";
import DrawerFormTitle from "../../../../Components/Common/DrawerFormTitle/DrawerFormTitle";
import TextFieldRenderer from "../../../../Components/Form/TextFieldRenderer";
import MultipleFilesUpload from "../../../../Components/Form/MultipleFilesUpload";
import LocationRenderer from "../../../../Components/Form/LocationRenderer";

import { FILE_TYPES, imageMimeTypesDropzone } from "../../../../utility/Enum";
import { useTranslation } from "../../../../context/Internationalization";
import { SnackBarContext } from "../../../../context/SnackBarContext";
import useGraphql from "../../../../Hooks/useGraphql";
import useLog from "../../../../Hooks/useLog";

const schema = (t) => [
  {
    label: t("field_name"),
    name: "name",
    validators: [
      {
        validator: isEmpty,
        text: (label) => `${label} ${t("cannot_be_empty")}`,
      },
    ],
    renderer: TextFieldRenderer,
    row: 1,
  },
  {
    label: t("location"),
    name: "location",
    validators: [
      {
        validator: isEmptyObject,
        text: (label) => `${label} ${t("cannot_be_empty")}`,
      },
    ],
    renderer: LocationRenderer,
    row: 2,
  },
  {
    label: t("field_photo"),
    name: "fieldPhoto",
    renderer: MultipleFilesUpload,
    singleFileUpload: true,
    acceptedFileTypes: imageMimeTypesDropzone,
    row: 3,
  },
];

const initialState = {
  name: "",
  fieldPhoto: null,
  initialFieldPhoto: null,
  location: {},
  errors: {},
};

const populateData = (field) => {
  const _initialState = { ...initialState };
  if (field) {
    _initialState.name = field.name;
    _initialState.fieldPhoto = field.photo ? [field.photo] : null;
    _initialState.initialFieldPhoto = _initialState.fieldPhoto;
    _initialState.location = {
      address: field.location?.address,
      lat: field.location?.coordinates[1],
      lng: field.location?.coordinates[0],
    };
  }
  return _initialState;
};

const FieldsDrawer = ({
  openDrawer,
  onCloseDrawer,
  selectedField,
  getFields,
}) => {
  const snackBar = useContext(SnackBarContext);
  const [values, setValues] = useState(populateData(selectedField));
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const { axiosGraphQL } = useGraphql();
  const { errorLog } = useLog();
  const _schema = schema(t);

  useEffect(() => {
    if (selectedField) {
      setValues(populateData(selectedField));
    } else {
      setValues(initialState);
    }
    setLoading(false);
  }, [selectedField?._id]);

  const onClose = () => {
    if (loading) return;
    setValues(initialState);
    onCloseDrawer();
  };

  const handleSubmit = async () => {
    if (loading) return;
    const errors = formValidator({ schema: _schema, values });
    if (Object.keys(errors).length > 0) {
      setValues((prevState) => ({ ...prevState, errors }));
      return;
    }

    setLoading(true);
    try {
      let query = CREATE_FIELD;
      let variables = {
        field: {
          name: values.name,
          location: {
            type: "Point",
            address: values.location.address,
            coordinates: [values.location.lng, values.location.lat],
          },
        },
      };

      if (selectedField) {
        query = EDIT_FIELD;
        variables = {
          fieldId: selectedField?._id,
          field: {
            name: values.name,
            location: {
              type: "Point",
              address: values.location.address,
              coordinates: [values.location.lng, values.location.lat],
            },
          },
        };
      }

      const resp = await axiosGraphQL({ query, variables });
      const fieldId =
        query === CREATE_FIELD
          ? resp.data.data.createField._id
          : selectedField._id;

      await uploadFieldPhoto(fieldId); // success toast + close inside
    } catch (error) {
      errorLog(error, "FieldsDrawer", "handleSubmit", "CREATE/EDIT_FIELD");
      snackBar.setOptions({
        alertMessage: error.message,
        alertSeverity: "error",
      });
      snackBar.setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const uploadFieldPhoto = async (fieldId) => {
    let alertMessage = t("field_created_successfully");
    if (selectedField?._id) alertMessage = t("field_updated_successfully");

    // delete old photo if needed
    try {
      if (values.initialFieldPhoto && values.initialFieldPhoto.length > 0) {
        const shouldBeDeleted =
          values.fieldPhoto?.length === 0 ||
          (values.fieldPhoto?.[0]?.name !== undefined &&
            values.fieldPhoto?.[0]?.name !==
              values.initialFieldPhoto[0].split("/").pop());
        if (shouldBeDeleted) {
          const deleteVariables = {
            deleteType: FILE_TYPES.clubFieldPhoto,
            itemId: fieldId,
            fileName: values.initialFieldPhoto[0].split("/").pop(),
          };
          await axiosGraphQL({
            query: DELETE_IMAGE,
            variables: deleteVariables,
          });
          if (values.fieldPhoto?.length === 0) {
            await getFields();
          }
        }
      }
    } catch (error) {
      errorLog(error, "FieldsDrawer", "uploadFieldPhoto", "DELETE_IMAGE");
      snackBar.setOptions({
        alertMessage: t("error_uploading_image"),
        alertSeverity: "error",
      });
      snackBar.setSnackbarOpen(true);
      // proceed — user may still be uploading a new one
    }

    // upload new photo (if provided)
    if (
      values.fieldPhoto &&
      values.fieldPhoto.length > 0 &&
      values.fieldPhoto[0].name
    ) {
      try {
        const variables = {
          uploadType: FILE_TYPES.clubFieldPhoto,
          itemId: fieldId,
          fileName: values.fieldPhoto[0].name,
        };
        const uploadResp = await axiosGraphQL({
          query: UPLOAD_IMAGE,
          variables,
        });
        const putUrl = uploadResp.data.data.uploadImage;

        // modern upload (no FileReader)
        const arrayBuffer = await values.fieldPhoto[0].arrayBuffer();
        const blob = new Blob([new Uint8Array(arrayBuffer)]);

        await fetch(putUrl, { method: "PUT", body: blob });

        snackBar.setOptions({ alertMessage, alertSeverity: "success" });
        snackBar.setSnackbarOpen(true);
        await getFields();
        onClose();
      } catch (error) {
        errorLog(error, "FieldsDrawer", "uploadFieldPhoto", "UPLOAD_IMAGE");
        snackBar.setOptions({
          alertMessage: t("error_uploading_image"),
          alertSeverity: "error",
        });
        snackBar.setSnackbarOpen(true);
      }
    } else {
      // no image to upload — just finish
      snackBar.setOptions({ alertMessage, alertSeverity: "success" });
      snackBar.setSnackbarOpen(true);
      await getFields();
      onClose();
    }
  };

  const onChange = ({ target: { name, value } }) => {
    if (loading) return;
    setValues((prevState) => ({ ...prevState, [name]: value }));
  };

  return (
    <DrawerForm openDrawer={openDrawer} onCloseDrawer={onClose}>
      <DrawerFormTitle
        actionText={selectedField ? t("update") : t("create")}
        className="grid-container-header"
        text={t("a_field")}
      />
      <Grid item xs={12}>
        <Form
          schema={_schema}
          values={values}
          onChange={onChange}
          errors={values.errors}
        />
      </Grid>
      <Grid item xs={12} style={{ paddingTop: 24 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          fullWidth
          disabled={loading}
          startIcon={loading ? <CircularProgress size={18} /> : null}
        >
          {loading ? t("saving") : t("save")}
        </Button>
      </Grid>
    </DrawerForm>
  );
};

export default FieldsDrawer;
